// src/app/colors/page.tsx
"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Badge } from "@/app/components/ui/badge";
import { Textarea } from "@/app/components/ui/textarea";
import { Separator } from "@/app/components/ui/separator";
import {
  Copy,
  Check,
  Palette,
  Download,
  Upload,
  Image as ImageIcon,
  Droplet,
  Plus,
  Trash2,
  Shuffle,
  Wand2,
  Link2,
  Undo2,
  SortAsc,
  SortDesc,
  Contrast,
  Eraser,
  Circle,
  Rows,
  Grid3X3,
  Sparkles,
} from "lucide-react";
import { Sketch } from "@uiw/react-color";

// -----------------------------
// Types
// -----------------------------
type RGB = { r: number; g: number; b: number };
type HSL = { h: number; s: number; l: number };
type OKLAB = { L: number; a: number; b: number };
type OKLCH = { l: number; c: number; h: number };

type ColorInfo = {
  hex: string;
  rgb: RGB;
  hsl: HSL;
  oklch: OKLCH;
  luminance: number;
  textOn: "black" | "white";
};

type PaletteItem = ColorInfo & { id: string };

type HarmonyMode =
  | "complementary"
  | "analogous"
  | "triad"
  | "tetrad"
  | "split-complementary"
  | "monochrome";

type SortMode = "none" | "hue" | "chroma" | "lightness";

type HistoryEntry = {
  id: string;
  name: string;
  createdAt: number;
  colors: string[]; // HEX
};

// -----------------------------
// Utils - numeric helpers
// -----------------------------
const clamp = (x: number, min = 0, max = 1) => Math.min(max, Math.max(min, x));
const toFixed = (n: number, d = 3) => Number(n.toFixed(d));
const deg = (rad: number) => (rad * 180) / Math.PI;
const rad = (degN: number) => (degN * Math.PI) / 180;
const wrapDeg = (h: number) => ((h % 360) + 360) % 360;

// -----------------------------
// Utils - parsing & formatting
// -----------------------------
function normalizeHex(input: string): string | null {
  const s = input.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(s)) return `#${s.toUpperCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(s))
    return (
      "#" +
      s
        .split("")
        .map((c) => c + c)
        .join("")
        .toUpperCase()
    );
  return null;
}

function rgbToHex({ r, g, b }: RGB): string {
  const f = (x: number) => x.toString(16).padStart(2, "0").toUpperCase();
  return `#${f(clamp(Math.round(r), 0, 255))}${f(clamp(Math.round(g), 0, 255))}${f(
    clamp(Math.round(b), 0, 255)
  )}`;
}

function hexToRgb(hex: string): RGB {
  const n = normalizeHex(hex);
  if (!n) return { r: 255, g: 255, b: 255 };
  const s = n.slice(1);
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}

function rgbToHsl({ r, g, b }: RGB): HSL {
  const R = r / 255,
    G = g / 255,
    B = b / 255;
  const max = Math.max(R, G, B),
    min = Math.min(R, G, B);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    switch (max) {
      case R:
        h = 60 * (((G - B) / d + 6) % 6);
        break;
      case G:
        h = 60 * ((B - R) / d + 2);
        break;
      case B:
        h = 60 * ((R - G) / d + 4);
        break;
    }
  }
  return { h: wrapDeg(h), s, l };
}

function hslToRgb({ h, s, l }: HSL): RGB {
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - C / 2;
  let [R, G, B] = [0, 0, 0];
  if (h < 60) [R, G, B] = [C, X, 0];
  else if (h < 120) [R, G, B] = [X, C, 0];
  else if (h < 180) [R, G, B] = [0, C, X];
  else if (h < 240) [R, G, B] = [0, X, C];
  else if (h < 300) [R, G, B] = [X, 0, C];
  else [R, G, B] = [C, 0, X];
  return {
    r: Math.round((R + m) * 255),
    g: Math.round((G + m) * 255),
    b: Math.round((B + m) * 255),
  };
}

// -----------------------------
// Utils - sRGB <-> OKLab/OKLCH (perceptual)
// -----------------------------
// Based on Björn Ottosson's OKLab reference implementation.
const srgbToLinear = (u: number) =>
  u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4);
const linearToSrgb = (u: number) =>
  u <= 0.0031308 ? 12.92 * u : 1.055 * Math.pow(u, 1 / 2.4) - 0.055;

function rgbToOklab({ r, g, b }: RGB): OKLAB {
  const R = srgbToLinear(r / 255);
  const G = srgbToLinear(g / 255);
  const B = srgbToLinear(b / 255);

  const l = 0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B;
  const m = 0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B;
  const s = 0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const b_ = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  return { L, a, b: b_ };
}

function oklabToRgb({ L, a, b }: OKLAB): RGB {
  const l_ = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
  const m_ = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
  const s_ = Math.pow(L - 0.0894841775 * a - 1.291485548 * b, 3);

  const R = +linearToSrgb(4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_);
  const G = +linearToSrgb(-1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_);
  const B = +linearToSrgb(-0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_);

  return {
    r: Math.round(clamp(R, 0, 1) * 255),
    g: Math.round(clamp(G, 0, 1) * 255),
    b: Math.round(clamp(B, 0, 1) * 255),
  };
}

function oklabToOklch(Lab: OKLAB): OKLCH {
  const { L, a, b } = Lab;
  const c = Math.sqrt(a * a + b * b);
  const h = (Math.atan2(b, a) * 180) / Math.PI;
  return { l: L, c, h: wrapDeg(h) };
}

function oklchToOklab({ l, c, h }: OKLCH): OKLAB {
  const hr = rad(h);
  return { L: l, a: c * Math.cos(hr), b: c * Math.sin(hr) };
}

// -----------------------------
// Utils - luminance & contrast
// -----------------------------
function relativeLuminance({ r, g, b }: RGB): number {
  const R = srgbToLinear(r / 255);
  const G = srgbToLinear(g / 255);
  const B = srgbToLinear(b / 255);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

function contrastRatio(fg: RGB, bg: RGB): number {
  const L1 = relativeLuminance(fg);
  const L2 = relativeLuminance(bg);
  const [hi, lo] = L1 >= L2 ? [L1, L2] : [L2, L1];
  return (hi + 0.05) / (lo + 0.05);
}

function idealText(hex: string): "black" | "white" {
  const rgb = hexToRgb(hex);
  return contrastRatio({ r: 0, g: 0, b: 0 }, rgb) >= 4.5 ? "black" : "white";
}

// -----------------------------
// Utils - color transforms
// -----------------------------
function withLightness(hex: string, factor: number): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  const l = clamp(hsl.l * factor, 0, 1);
  return rgbToHex(hslToRgb({ ...hsl, l }));
}

function tint(hex: string, amount = 0.15): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb({ ...hsl, l: clamp(hsl.l + amount, 0, 1) }));
}

function shade(hex: string, amount = 0.15): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb({ ...hsl, l: clamp(hsl.l - amount, 0, 1) }));
}

function shiftHue(hex: string, degAmt: number): string {
  const hsl = rgbToHsl(hexToRgb(hex));
  return rgbToHex(hslToRgb({ ...hsl, h: wrapDeg(hsl.h + degAmt) }));
}

// -----------------------------
// Utils - harmonies
// -----------------------------
function harmony(hex: string, mode: HarmonyMode): string[] {
  switch (mode) {
    case "complementary":
      return [hex, shiftHue(hex, 180)];
    case "analogous":
      return [shiftHue(hex, -30), hex, shiftHue(hex, 30)];
    case "triad":
      return [hex, shiftHue(hex, 120), shiftHue(hex, -120)];
    case "tetrad":
      return [hex, shiftHue(hex, 90), shiftHue(hex, 180), shiftHue(hex, -90)];
    case "split-complementary":
      return [hex, shiftHue(hex, 150), shiftHue(hex, -150)];
    case "monochrome":
      return [shade(hex, 0.2), hex, tint(hex, 0.2)];
  }
}

// -----------------------------
// Utils - OKLab distance
// -----------------------------
function deltaE_oklab(a: OKLAB, b: OKLAB): number {
  // Euclidean distance in OKLab (simple, perceptually decent for clustering)
  const dL = a.L - b.L;
  const da = a.a - b.a;
  const db = a.b - b.b;
  return Math.sqrt(dL * dL + da * da + db * db);
}

// -----------------------------
// Utils - conversions pack
// -----------------------------
function buildColorInfo(hex: string): ColorInfo {
  const safe = normalizeHex(hex) ?? "#FFFFFF";
  const rgb = hexToRgb(safe);
  const hsl = rgbToHsl(rgb);
  const oklab = rgbToOklab(rgb);
  const oklch = oklabToOklch(oklab);
  const luminance = relativeLuminance(rgb);
  const textOn = idealText(safe);
  return {
    hex: safe,
    rgb,
    hsl: { h: toFixed(hsl.h, 1), s: toFixed(hsl.s, 3), l: toFixed(hsl.l, 3) },
    oklch: { l: toFixed(oklch.l, 3), c: toFixed(oklch.c, 3), h: toFixed(oklch.h, 1) },
    luminance: toFixed(luminance, 4),
    textOn,
  };
}

function fmtRGB(rgb: RGB) {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}
function fmtHSL(hsl: HSL) {
  return `hsl(${toFixed(hsl.h, 1)} ${toFixed(hsl.s * 100, 1)}% ${toFixed(hsl.l * 100, 1)}%)`;
}
function fmtOKLCH(oklch: OKLCH) {
  return `oklch(${toFixed(oklch.l, 3)} ${toFixed(oklch.c, 3)} ${toFixed(oklch.h, 1)})`;
}

// -----------------------------
// K-means palette extraction in OKLab
// -----------------------------
async function extractPaletteFromCanvas(
  canvas: HTMLCanvasElement,
  k = 8,
  maxIters = 10,
  sampleStep = 4
): Promise<string[]> {
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;

  // Sample pixels
  const samples: OKLAB[] = [];
  for (let y = 0; y < height; y += sampleStep) {
    for (let x = 0; x < width; x += sampleStep) {
      const idx = (y * width + x) * 4;
      const r = data[idx],
        g = data[idx + 1],
        b = data[idx + 2],
        a = data[idx + 3];
      if (a < 8) continue;
      const lab = rgbToOklab({ r, g, b });
      samples.push(lab);
    }
  }
  if (samples.length === 0) return [];

  // Init centroids from random samples
  const centroids: OKLAB[] = [];
  const used = new Set<number>();
  while (centroids.length < k && used.size < samples.length) {
    const i = Math.floor(Math.random() * samples.length);
    if (!used.has(i)) {
      centroids.push({ ...samples[i] });
      used.add(i);
    }
  }

  const assignments = new Array<number>(samples.length).fill(0);

  for (let iter = 0; iter < maxIters; iter++) {
    // Assign
    for (let i = 0; i < samples.length; i++) {
      let best = 0;
      let bestD = Infinity;
      for (let c = 0; c < centroids.length; c++) {
        const d = deltaE_oklab(samples[i], centroids[c]);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      assignments[i] = best;
    }
    // Update
    const sum: { L: number; a: number; b: number; n: number }[] = centroids.map(() => ({
      L: 0,
      a: 0,
      b: 0,
      n: 0,
    }));
    for (let i = 0; i < samples.length; i++) {
      const c = assignments[i];
      const s = samples[i];
      sum[c].L += s.L;
      sum[c].a += s.a;
      sum[c].b += s.b;
      sum[c].n++;
    }
    for (let c = 0; c < centroids.length; c++) {
      if (sum[c].n > 0) {
        centroids[c] = {
          L: sum[c].L / sum[c].n,
          a: sum[c].a / sum[c].n,
          b: sum[c].b / sum[c].n,
        };
      }
    }
  }

  // Convert centroids back to hex
  const hexes = centroids
    .map((lab) => rgbToHex(oklabToRgb(lab)))
    .filter((v, i, arr) => arr.indexOf(v) === i); // unique

  return hexes.slice(0, k);
}

// -----------------------------
// Download helpers
// -----------------------------
function downloadText(filename: string, text: string) {
  // Determine MIME type based on file extension
  const getMimeType = (filename: string): string => {
    const ext = filename.toLowerCase().split('.').pop();
    switch (ext) {
      case 'json':
        return 'application/json;charset=utf-8';
      case 'css':
        return 'text/css;charset=utf-8';
      case 'svg':
        return 'image/svg+xml;charset=utf-8';
      case 'txt':
        return 'text/plain;charset=utf-8';
      case 'html':
        return 'text/html;charset=utf-8';
      case 'js':
        return 'application/javascript;charset=utf-8';
      case 'ts':
        return 'application/typescript;charset=utf-8';
      default:
        return 'text/plain;charset=utf-8';
    }
  };

  const blob = new Blob([text], { type: getMimeType(filename) });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// -----------------------------
// Component
// -----------------------------
export default function ColorPage() {
  // Core state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [picked, setPicked] = useState<string>("#7F5AF0");
  const [palette, setPalette] = useState<PaletteItem[]>([]);
  const [sortMode, setSortMode] = useState<SortMode>("none");
  const [copied, setCopied] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [copyAnimations, setCopyAnimations] = useState<Set<string>>(new Set());
  const [harmonyMode, setHarmonyMode] = useState<HarmonyMode>("analogous");
  const [gradientAngle, setGradientAngle] = useState<number>(90);

  // Contrast tool
  const [contrastFg, setContrastFg] = useState<string>("#000000");
  const [contrastBg, setContrastBg] = useState<string>("#FFFFFF");

  // History
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyName, setHistoryName] = useState<string>("Untitled Palette");

  // Refs
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load palette from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("p"); // expected comma-separated hex without '#'
    if (p) {
      const colors = p
        .split(",")
        .map((s) => normalizeHex(`#${s}`))
        .filter(Boolean) as string[];
      if (colors.length > 0) {
        setPalette(
          colors.map((hex, idx) => ({ id: `url-${idx}`, ...buildColorInfo(hex) }))
        );
      }
    }
  }, []);

  // Persist history
  useEffect(() => {
    const raw = localStorage.getItem("paletteHistory");
    if (raw) {
      try {
        setHistory(JSON.parse(raw) as HistoryEntry[]);
      } catch {
        // ignore
      }
    }
  }, []);
  useEffect(() => {
    localStorage.setItem("paletteHistory", JSON.stringify(history));
  }, [history]);

  // Draw uploaded image to canvas
  useEffect(() => {
    if (!imageSrc || !imgRef.current || !canvasRef.current) return;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    img.onload = () => {
      // scale down oversized images for speed
      const maxW = 1280;
      const scale = Math.min(1, maxW / img.naturalWidth);
      canvas.width = Math.max(1, Math.floor(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.floor(img.naturalHeight * scale));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
  }, [imageSrc]);

  // Derived
  const pickedInfo = useMemo(() => buildColorInfo(picked), [picked]);

  const sortedPalette = useMemo(() => {
    if (sortMode === "none") return palette;
    const clone = [...palette];
    if (sortMode === "hue") {
      clone.sort((a, b) => a.hsl.h - b.hsl.h);
    } else if (sortMode === "lightness") {
      clone.sort((a, b) => a.oklch.l - b.oklch.l);
    } else if (sortMode === "chroma") {
      clone.sort((a, b) => a.oklch.c - b.oklch.c);
    }
    return clone;
  }, [palette, sortMode]);

  // Handlers
  const copy = useCallback((text: string, key?: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    
    // Add animation for specific copy actions
    if (key) {
      setCopyAnimations(prev => new Set(prev).add(key));
      setTimeout(() => {
        setCopyAnimations(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      }, 600);
    }
    
    setTimeout(() => setCopied(null), 1500);
  }, []);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setImageSrc(url);
  };

  const onImageClick = (e: React.MouseEvent) => {
    if (!imgRef.current || !canvasRef.current) return;
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    const x = Math.floor(((e.clientX - rect.left) / rect.width) * img.naturalWidth);
    const y = Math.floor(((e.clientY - rect.top) / rect.height) * img.naturalHeight);
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;
    const [r, g, b] = ctx.getImageData(x, y, 1, 1).data;
    setPicked(rgbToHex({ r, g, b }));
  };

  const doExtract = async () => {
    if (!canvasRef.current) return;
    setIsExtracting(true);
    try {
      const colors = await extractPaletteFromCanvas(canvasRef.current, 8, 10, 6);
      setPalette(colors.map((hex, i) => ({ id: `ex-${i}`, ...buildColorInfo(hex) })));
    } catch (err) {
      console.error(err);
    } finally {
      setIsExtracting(false);
    }
  };

  const addPickedToPalette = () => {
    const hex = normalizeHex(picked);
    if (!hex) return;
    setPalette((p) => [{ id: crypto.randomUUID(), ...buildColorInfo(hex) }, ...p]);
  };

  const removeFromPalette = (id: string) => {
    setPalette((p) => p.filter((c) => c.id !== id));
  };

  const generateRandomPalette = () => {
    const rand = () => Math.floor(Math.random() * 256);
    const randomHex = () => rgbToHex({ r: rand(), g: rand(), b: rand() });
    const arr = Array.from({ length: 8 }, () => randomHex());
    setPalette(arr.map((hex) => ({ id: crypto.randomUUID(), ...buildColorInfo(hex) })));
  };

  const applyHarmony = () => {
    const list = harmony(picked, harmonyMode);
    setPalette(list.map((hex, i) => ({ id: `h-${i}`, ...buildColorInfo(hex) })));
  };

  const clearPalette = () => setPalette([]);

  const saveHistory = () => {
    const entry: HistoryEntry = {
      id: crypto.randomUUID(),
      name: historyName.trim() || "Untitled Palette",
      createdAt: Date.now(),
      colors: palette.map((c) => c.hex),
    };
    setHistory((h) => [entry, ...h].slice(0, 50));
  };

  const loadHistory = (entry: HistoryEntry) => {
    setPalette(entry.colors.map((hex, i) => ({ id: `h-${i}`, ...buildColorInfo(hex) })));
  };

  const deleteHistory = (id: string) => {
    setHistory((h) => h.filter((e) => e.id !== id));
  };

  // EyeDropper API
  const [eyedropperAvailable, setEyedropperAvailable] = useState(false);
  
  useEffect(() => {
    setEyedropperAvailable(typeof window !== "undefined" && "EyeDropper" in window);
  }, []);
  const openEyeDropper = async () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const EyeDropperCtor = (window as any).EyeDropper;
      const ed = new EyeDropperCtor();
      const res = await ed.open();
      setPicked(res.sRGBHex);
    } catch {
      // cancelled or unsupported
    }
  };

  // Exports
  const exportJSON = () => {
    const payload = {
      name: historyName || "Palette",
      colors: palette.map((c) => ({
        hex: c.hex,
        rgb: c.rgb,
        hsl: c.hsl,
        oklch: c.oklch,
      })),
    };
    downloadText(`${(historyName || "palette").replace(/\s+/g, "-")}.json`, JSON.stringify(payload, null, 2));
  };

  const exportCSS = () => {
    const lines = [
      `:root {`,
      ...palette.map((c, i) => `  --color-${i + 1}: ${c.hex};`),
      ...palette.map(
        (c, i) =>
          `  --oklch-${i + 1}: ${fmtOKLCH(c.oklch)}; /* Tailwind v4 tokens friendly */`
      ),
      `}`,
    ];
    downloadText(`${(historyName || "palette").replace(/\s+/g, "-")}.css`, lines.join("\n"));
  };

  const exportSVG = () => {
    const sw = 120;
    const sh = 80;
    const svgW = sw * palette.length;
    const svg = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${svgW}" height="${sh}">`,
      ...palette.map(
        (c, i) =>
          `<rect x="${i * sw}" y="0" width="${sw}" height="${sh}" fill="${c.hex}"/>`
      ),
      `</svg>`,
    ].join("");
    downloadText(`${(historyName || "palette").replace(/\s+/g, "-")}.svg`, svg);
  };

  const exportURL = () => {
    const qs = palette.map((c) => c.hex.slice(1)).join(",");
    const url = `${location.origin}${location.pathname}?p=${qs}`;
    copy(url, 'share-url');
  };

  // Import
  const importTextRef = useRef<HTMLTextAreaElement>(null);
  const doImport = () => {
    const text = importTextRef.current?.value ?? "";
    const matches = text.match(/#?[0-9a-fA-F]{3,6}/g) ?? [];
    const hexes = matches
      .map((m) => normalizeHex(m))
      .filter(Boolean) as string[];
    if (hexes.length) {
      setPalette(hexes.map((hex) => ({ id: crypto.randomUUID(), ...buildColorInfo(hex) })));
    }
  };

  // Contrast checker derived values
  const contrastRatioValue = useMemo(() => {
    return toFixed(contrastRatio(hexToRgb(contrastFg), hexToRgb(contrastBg)), 2);
  }, [contrastFg, contrastBg]);
  const contrastAA = contrastRatioValue >= 4.5;
  const contrastAAA = contrastRatioValue >= 7;

  // Gradient
  const gradientCSS = useMemo(() => {
    const stops = (sortedPalette.length ? sortedPalette : palette).map((c) => c.hex);
    if (!stops.length) return "";
    return `linear-gradient(${gradientAngle}deg, ${stops.join(", ")})`;
  }, [sortedPalette, palette, gradientAngle]);

  // Swatch subcomponent
  const Swatch: React.FC<{ c: PaletteItem }> = ({ c }) => {
    const menu = [
      { label: "HEX", value: c.hex },
      { label: "RGB", value: fmtRGB(c.rgb) },
      { label: "HSL", value: fmtHSL(c.hsl) },
      { label: "OKLCH", value: fmtOKLCH(c.oklch) },
    ];
    return (
      <Card className="overflow-hidden group hover:shadow-md transition-shadow">
        <div
          className="h-20 w-full"
          style={{ backgroundColor: c.hex }}
          title={c.hex}
        />
        <CardContent className="p-2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs">{c.hex}</span>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="outline"
                onClick={() => copy(c.hex, `swatch-${c.id}-hex`)}
                className={`h-6 w-6 transition-all duration-300 ${copyAnimations.has(`swatch-${c.id}-hex`) ? 'scale-110 bg-green-100 dark:bg-green-900' : ''}`}
                title="Copy HEX"
              >
                {copied === c.hex ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => removeFromPalette(c.id)}
                className="h-6 w-6"
                title="Remove"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {menu.map((m) => (
              <Button
                key={m.label}
                size="sm"
                variant="secondary"
                onClick={() => copy(m.value, `swatch-${c.id}-${m.label.toLowerCase()}`)}
                className={`justify-start h-6 text-xs transition-all duration-300 ${copyAnimations.has(`swatch-${c.id}-${m.label.toLowerCase()}`) ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
              >
                <Copy className="h-3 w-3 mr-1" />
                {m.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs opacity-70">Text</span>
            <Badge variant="outline" className="text-xs">
              {c.textOn.toUpperCase()}
            </Badge>
          </div>
        </CardContent>
      </Card>
    );
  };

  // UI
  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold">Ultimate Color Palette Generator</h1>
          <p className="text-muted-foreground text-lg mt-2">
            Extract, compose, validate, and export palettes with pro-grade tools.
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left: Pickers & Image */}
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Select Colors
              </CardTitle>
              <CardDescription>Manual, EyeDropper, or from an image.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Manual */}
              <div className="space-y-3">
                <Label>Manual Color</Label>
                <div className="flex gap-2 items-center">
                  <div className="relative">
                    <div
                      className="w-16 h-10 rounded border cursor-pointer hover:shadow-sm"
                      style={{ backgroundColor: picked }}
                      onClick={() => setShowPicker((s) => !s)}
                      title="Open color picker"
                    />
                    {showPicker && (
                      <div className="absolute z-50 mt-2">
                        <Sketch
                          color={picked}
                          onChange={(c) => setPicked(c.hex)}
                        />
                      </div>
                    )}
                  </div>
                  <Input
                    value={picked}
                    onChange={(e) => setPicked(normalizeHex(e.target.value) ?? e.target.value)}
                    className="font-mono"
                    placeholder="#AABBCC"
                  />
                  <Button 
                    onClick={() => copy(picked, 'manual-picker')} 
                    variant="outline" 
                    size="sm"
                    className={`transition-all duration-300 ${copyAnimations.has('manual-picker') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
                  >
                    {copied === picked ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button onClick={addPickedToPalette} size="sm" className="btn-enhanced hover-lift">
                    <Plus className="h-4 w-4 mr-1" /> Add to Palette
                  </Button>
                  <Button
                    onClick={generateRandomPalette}
                    variant="outline"
                    size="sm"
                    className="btn-enhanced hover-lift"
                  >
                    <Shuffle className="h-4 w-4 mr-1" /> Random 8
                  </Button>
                </div>
                {eyedropperAvailable && (
                  <Button onClick={openEyeDropper} variant="secondary" size="sm">
                    <Droplet className="h-4 w-4 mr-1" /> System Eyedropper
                  </Button>
                )}
              </div>

              <Separator />

              {/* Harmony */}
              <div className="space-y-3">
                <Label>Harmony</Label>
                <div className="flex flex-wrap gap-2">
                  <Select value={harmonyMode} onValueChange={(value) => setHarmonyMode(value as HarmonyMode)}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select harmony" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="analogous">Analogous</SelectItem>
                      <SelectItem value="complementary">Complementary</SelectItem>
                      <SelectItem value="split-complementary">Split-Complementary</SelectItem>
                      <SelectItem value="triad">Triad</SelectItem>
                      <SelectItem value="tetrad">Tetrad</SelectItem>
                      <SelectItem value="monochrome">Monochrome</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={applyHarmony} size="sm">
                    <Wand2 className="h-4 w-4 mr-1" /> Apply
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Image upload/extract */}
              <div className="space-y-3">
                <Label>From Image</Label>
                <div className="flex items-center gap-2">
                  <Input type="file" accept="image/*" onChange={handleImageUpload} />
                  <Button
                    onClick={doExtract}
                    disabled={!imageSrc || isExtracting}
                    className="btn-enhanced hover-lift"
                  >
                    {isExtracting ? "Extracting..." : "Extract Palette"}
                  </Button>
                  <Button
                    onClick={() => setImageSrc(null)}
                    variant="outline"
                    title="Clear image"
                  >
                    <Eraser className="h-4 w-4 mr-1" /> Clear
                  </Button>
                </div>
                {imageSrc && (
                  <div>
                    <div className="relative rounded-lg overflow-hidden border">
                      <img
                        ref={imgRef}
                        src={imageSrc}
                        alt="Uploaded"
                        className="w-full h-64 object-cover cursor-crosshair"
                        onClick={onImageClick}
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 text-xs bg-black/60 text-white rounded">
                        Click image to sample a pixel
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm">
                      <ImageIcon className="h-4 w-4 opacity-70" />
                      <span className="opacity-70">Picked: </span>
                      <span className="font-mono">{picked}</span>
                      <div
                        className="h-4 w-4 rounded border"
                        style={{ backgroundColor: picked }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right: Current Color + Contrast */}
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Circle className="h-5 w-5" />
                Current Color
              </CardTitle>
              <CardDescription>Formats, tints/shades, and details.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div
                className="w-full h-32 rounded-lg border"
                style={{ backgroundColor: pickedInfo.hex }}
              />
              <div className="space-y-4">
                {/* Color Values */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">HEX</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{pickedInfo.hex}</span>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className={`h-7 w-7 transition-all duration-300 ${copyAnimations.has('hex') ? 'scale-110 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(pickedInfo.hex, 'hex')}
                        >
                          {copied === pickedInfo.hex ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">RGB</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{fmtRGB(pickedInfo.rgb)}</span>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className={`h-7 w-7 transition-all duration-300 ${copyAnimations.has('rgb') ? 'scale-110 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(fmtRGB(pickedInfo.rgb), 'rgb')}
                        >
                          {copied === fmtRGB(pickedInfo.rgb) ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">HSL</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{fmtHSL(pickedInfo.hsl)}</span>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className={`h-7 w-7 transition-all duration-300 ${copyAnimations.has('hsl') ? 'scale-110 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(fmtHSL(pickedInfo.hsl), 'hsl')}
                        >
                          {copied === fmtHSL(pickedInfo.hsl) ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">OKLCH</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{fmtOKLCH(pickedInfo.oklch)}</span>
                        <Button 
                          size="icon" 
                          variant="outline" 
                          className={`h-7 w-7 transition-all duration-300 ${copyAnimations.has('oklch') ? 'scale-110 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(fmtOKLCH(pickedInfo.oklch), 'oklch')}
                        >
                          {copied === fmtOKLCH(pickedInfo.oklch) ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">Luminance</span>
                      <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{pickedInfo.luminance}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-muted-foreground">Ideal Text</span>
                      <Badge variant="outline" className="text-xs">
                        {pickedInfo.textOn.toUpperCase()}
                      </Badge>
                    </div>
                    
                    {/* Quick Copy Buttons */}
                    <div className="space-y-2">
                      <div className="text-xs text-muted-foreground">Quick Copy</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`transition-all duration-300 ${copyAnimations.has('quick-hex') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(pickedInfo.hex, 'quick-hex')}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          HEX
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`transition-all duration-300 ${copyAnimations.has('quick-rgb') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(fmtRGB(pickedInfo.rgb), 'quick-rgb')}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          RGB
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`transition-all duration-300 ${copyAnimations.has('quick-hsl') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(fmtHSL(pickedInfo.hsl), 'quick-hsl')}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          HSL
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className={`transition-all duration-300 ${copyAnimations.has('quick-oklch') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
                          onClick={() => copy(fmtOKLCH(pickedInfo.oklch), 'quick-oklch')}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          OKLCH
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Tints & Shades */}
              <div className="space-y-2">
                <Label>Tints & Shades</Label>
                <div className="grid grid-cols-5 gap-2">
                  {[0.7, 0.85, 1, 1.15, 1.3].map((f, i) => {
                    const color = withLightness(picked, f);
                    const isAnimating = copyAnimations.has(`tint-${i}`);
                    return (
                      <div key={i} className="text-center space-y-1">
                        <div
                          className={`h-12 rounded border cursor-pointer transition-all duration-300 hover:scale-105 ${isAnimating ? 'scale-110 ring-2 ring-green-500 ring-offset-2' : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => copy(color, `tint-${i}`)}
                          title="Click to copy"
                        />
                        <div className="text-[10px] font-mono bg-muted px-1 py-0.5 rounded">{color}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Contrast checker */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2"><Contrast className="h-4 w-4" /> Contrast Checker</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="text-xs opacity-70">Foreground</div>
                    <div className="flex gap-2 items-center">
                      <div className="w-8 h-8 rounded border" style={{ background: contrastFg }} />
                      <Input
                        value={contrastFg}
                        onChange={(e) => setContrastFg(normalizeHex(e.target.value) ?? e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-xs opacity-70">Background</div>
                    <div className="flex gap-2 items-center">
                      <div className="w-8 h-8 rounded border" style={{ background: contrastBg }} />
                      <Input
                        value={contrastBg}
                        onChange={(e) => setContrastBg(normalizeHex(e.target.value) ?? e.target.value)}
                        className="font-mono"
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="rounded border p-3"
                  style={{ background: contrastBg, color: idealText(contrastBg) }}
                >
                  <div style={{ color: contrastFg }}>
                    <div className="font-semibold">Ratio: {contrastRatioValue}:1</div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant={contrastAA ? "default" : "destructive"}>
                        AA: {contrastAA ? "Pass" : "Fail"}
                      </Badge>
                      <Badge variant={contrastAAA ? "default" : "destructive"}>
                        AAA: {contrastAAA ? "Pass" : "Fail"}
                      </Badge>
                    </div>
                    <p className="mt-2 text-sm">
                      The quick brown fox jumps over the lazy dog.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Palette */}
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Rows className="h-5 w-5" />
              Palette
            </CardTitle>
            <CardDescription>
              Sort, copy, export, and validate your working set.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Label>Sort</Label>
              <Select value={sortMode} onValueChange={(value) => setSortMode(value as SortMode)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="hue">Hue</SelectItem>
                  <SelectItem value="chroma">Chroma (OKLCH C)</SelectItem>
                  <SelectItem value="lightness">Lightness (OKLCH L)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={() => setPalette([...palette])}>
                <SortAsc className="h-4 w-4 mr-1" /> Apply
              </Button>
              <div className="ml-auto flex gap-2">
                <Input
                  placeholder="Palette name"
                  value={historyName}
                  onChange={(e) => setHistoryName(e.target.value)}
                />
                <Button size="sm" onClick={saveHistory}>
                  <Sparkles className="h-4 w-4 mr-1" /> Save to History
                </Button>
                <Button size="sm" variant="outline" onClick={clearPalette}>
                  <Undo2 className="h-4 w-4 mr-1" /> Clear
                </Button>
              </div>
            </div>

            {palette.length === 0 ? (
              <div className="text-sm text-muted-foreground">No colors yet. Add via picker, harmony, random, or image extraction.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {sortedPalette.map((c) => (
                  <Swatch key={c.id} c={c} />
                ))}
              </div>
            )}

            {/* Exports */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={exportJSON}>
                <Download className="h-4 w-4 mr-1" /> Export JSON
              </Button>
              <Button size="sm" variant="outline" onClick={exportCSS}>
                <Download className="h-4 w-4 mr-1" /> Export CSS / Tokens
              </Button>
              <Button size="sm" variant="outline" onClick={exportSVG}>
                <Download className="h-4 w-4 mr-1" /> Export SVG
              </Button>
              <Button 
                size="sm" 
                variant="secondary" 
                onClick={exportURL}
                className={`transition-all duration-300 ${copyAnimations.has('share-url') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
              >
                <Link2 className="h-4 w-4 mr-1" /> Copy Share URL
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Gradient Builder + Import */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Grid3X3 className="h-5 w-5" />
                Gradient Builder
              </CardTitle>
              <CardDescription>Use your palette as stops.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Label>Angle</Label>
                <Input
                  type="number"
                  min={0}
                  max={360}
                  value={gradientAngle}
                  onChange={(e) => setGradientAngle(Number(e.target.value))}
                  className="w-28"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setGradientAngle(90)}
                >
                  Reset
                </Button>
              </div>
              <div
                className="h-28 w-full rounded border"
                style={{ backgroundImage: gradientCSS || undefined }}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => copy(`background: ${gradientCSS};`, 'gradient-css')}
                  disabled={!gradientCSS}
                  className={`transition-all duration-300 ${copyAnimations.has('gradient-css') ? 'scale-105 bg-green-100 dark:bg-green-900' : ''}`}
                >
                  <Copy className="h-4 w-4 mr-1" /> Copy CSS
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="card-enhanced">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Import / Paste Colors
              </CardTitle>
              <CardDescription>Paste any HEX list or CSS and parse.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                ref={importTextRef}
                className="w-full h-28 font-mono text-sm"
                placeholder="#FF00FF, #0af, rgb(10,20,30) /* rgb ignored, HEX parsed */"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={doImport}>
                  <Upload className="h-4 w-4 mr-1" /> Import
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (importTextRef.current) importTextRef.current.value = "";
                  }}
                >
                  <Eraser className="h-4 w-4 mr-1" /> Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* History */}
        <Card className="card-enhanced">
          <CardHeader>
            <CardTitle>History</CardTitle>
            <CardDescription>Saved palettes (local only).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {history.length === 0 ? (
              <div className="text-sm text-muted-foreground">No saved palettes yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {history.map((h) => (
                  <Card key={h.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold">{h.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(h.createdAt).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => loadHistory(h)}>
                            Load
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => deleteHistory(h.id)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-8 gap-1">
                        {h.colors.map((c, i) => {
                          const isAnimating = copyAnimations.has(`history-${h.id}-${i}`);
                          return (
                            <div
                              key={i}
                              className={`h-8 rounded border hover:scale-105 transition-all duration-300 cursor-pointer ${isAnimating ? 'scale-110 ring-2 ring-green-500 ring-offset-1' : ''}`}
                              style={{ backgroundColor: c }}
                              title={c}
                              onClick={() => copy(c, `history-${h.id}-${i}`)}
                            />
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
}
